import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Bell, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthStore } from '@/store/authStore';
import {
  getAlerts,
  acknowledgeAlert,
  acknowledgeAllForProject,
} from '@/services/db/alerts';
import {
  ALERT_TYPE_LABEL,
  type Alert,
  type AlertSeverity,
} from '@/types/alerts';
import { supabase } from '@/services/db/supabase';
import DSRDetailPanel from '@/components/dashboard/DSRDetailPanel';
import { useDsrPanel } from '@/components/dashboard/useDsrPanel';

type SeverityFilter = 'all' | AlertSeverity;
type AckFilter = 'all' | 'unack' | 'ack';

const SEVERITY_BADGE: Record<AlertSeverity, string> = {
  critical: 'bg-red-600 text-white hover:bg-red-600',
  high: 'bg-orange-500 text-white hover:bg-orange-500',
  medium: 'bg-amber-500 text-white hover:bg-amber-500',
  low: 'bg-gray-500 text-white hover:bg-gray-500',
};

const BULK_ACK_ROLES = new Set(['supervisor', 'safety_manager', 'tenant_admin']);

const SEVERITY_VALUES = ['critical', 'high', 'medium', 'low'] as const;

export default function AlertsPage() {
  const { tenantId, userRole } = useAuthStore();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { dsrId: panelDsrId, openDsr, closeDsr } = useDsrPanel();
  // Drill-downs from the dashboards (S82b) navigate with ?severity=,
  // ?project=, and ?ack= URL params. Seed filter state from them on mount,
  // and re-apply when params change in the same session.
  const initialSeverity = (searchParams.get('severity') ?? 'all') as SeverityFilter;
  const initialAck = (searchParams.get('ack') ?? 'unack') as AckFilter;
  const initialProject = searchParams.get('project') ?? 'all';
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>(
    SEVERITY_VALUES.includes(initialSeverity as AlertSeverity) || initialSeverity === 'all'
      ? initialSeverity
      : 'all',
  );
  const [ackFilter, setAckFilter] = useState<AckFilter>(
    ['all', 'ack', 'unack'].includes(initialAck) ? initialAck : 'unack',
  );
  const [projectFilter, setProjectFilter] = useState<string>(initialProject);

  useEffect(() => {
    const sev = searchParams.get('severity');
    if (sev && (sev === 'all' || SEVERITY_VALUES.includes(sev as AlertSeverity))) {
      setSeverityFilter(sev as SeverityFilter);
    }
    const proj = searchParams.get('project');
    if (proj) setProjectFilter(proj);
    const ack = searchParams.get('ack');
    if (ack && ['all', 'ack', 'unack'].includes(ack)) {
      setAckFilter(ack as AckFilter);
    }
  }, [searchParams]);

  const projectsQ = useQuery({
    queryKey: ['alerts', 'projects', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name')
        .eq('is_deleted', false)
        .eq('is_active', true)
        .order('name');
      if (error) throw new Error(error.message);
      return (data ?? []) as Array<{ id: string; name: string }>;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  const alertsQ = useQuery({
    queryKey: [
      'alerts', 'list', tenantId, severityFilter, ackFilter, projectFilter,
    ],
    queryFn: () => getAlerts({
      tenantId: tenantId as string,
      severity: severityFilter === 'all' ? undefined : [severityFilter],
      acknowledged:
        ackFilter === 'unack' ? false : ackFilter === 'ack' ? true : undefined,
      projectIds: projectFilter === 'all' ? undefined : [projectFilter],
      limit: 200,
    }),
    enabled: !!tenantId,
    refetchInterval: 60_000,
  });

  const ackMut = useMutation({
    mutationFn: (id: string) => acknowledgeAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const ackAllMut = useMutation({
    mutationFn: (projectId: string) =>
      acknowledgeAllForProject(projectId, tenantId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const projectNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projectsQ.data ?? []) m.set(p.id, p.name);
    return m;
  }, [projectsQ.data]);

  const canBulkAck = !!userRole && BULK_ACK_ROLES.has(userRole);
  const alerts = alertsQ.data ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-mozaic-teal" />
          <h1 className="text-2xl font-semibold">Alerts</h1>
        </div>
        {canBulkAck && projectFilter !== 'all' && alerts.some((a) => !a.acknowledged_at) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => ackAllMut.mutate(projectFilter)}
            disabled={ackAllMut.isPending}
          >
            <Check className="mr-1.5 h-4 w-4" />
            Acknowledge all for this project
          </Button>
        )}
      </header>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 rounded-md border bg-muted/30 p-3">
        <FilterGroup
          label="Severity"
          value={severityFilter}
          onChange={(v) => setSeverityFilter(v as SeverityFilter)}
          options={[
            { value: 'all', label: 'All' },
            { value: 'critical', label: 'Critical' },
            { value: 'high', label: 'High' },
            { value: 'medium', label: 'Medium' },
            { value: 'low', label: 'Low' },
          ]}
        />
        <FilterGroup
          label="Status"
          value={ackFilter}
          onChange={(v) => setAckFilter(v as AckFilter)}
          options={[
            { value: 'all', label: 'All' },
            { value: 'unack', label: 'Unacknowledged' },
            { value: 'ack', label: 'Acknowledged' },
          ]}
        />
        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs font-medium text-muted-foreground">Project</label>
          <select
            className="h-8 rounded-md border bg-white px-2 text-sm"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <option value="all">All projects</option>
            {(projectsQ.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* List */}
      {alertsQ.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : alertsQ.isError ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-10 text-sm text-muted-foreground">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Unable to load alerts.
            <Button size="sm" variant="outline" onClick={() => alertsQ.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : alerts.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            No alerts — everything looks good.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.map((a) => (
            <AlertCard
              key={a.id}
              alert={a}
              projectName={projectNameById.get(a.project_id) ?? 'Unknown project'}
              onAck={() => ackMut.mutate(a.id)}
              acking={ackMut.isPending && ackMut.variables === a.id}
              onOpenSourceDsr={openDsr}
            />
          ))}
        </div>
      )}

      <DSRDetailPanel dsrId={panelDsrId} onClose={closeDsr} />
    </div>
  );
}

interface FilterGroupProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}

function FilterGroup({ label, value, onChange, options }: FilterGroupProps) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}:</span>
      <div className="flex rounded-md border bg-white p-0.5">
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`rounded px-2.5 py-1 text-xs transition-colors ${
              value === opt.value
                ? 'bg-mozaic-teal text-white'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface AlertCardProps {
  alert: Alert;
  projectName: string;
  onAck: () => void;
  acking: boolean;
  onOpenSourceDsr: (dsrId: string) => void;
}

function AlertCard({ alert, projectName, onAck, acking, onOpenSourceDsr }: AlertCardProps) {
  const relative = formatDistanceToNow(new Date(alert.created_at), { addSuffix: true });
  const isAck = !!alert.acknowledged_at;

  return (
    <Card className={isAck ? 'opacity-60' : ''}>
      <CardContent className="flex gap-4 py-4">
        <div className="flex flex-col items-start gap-2">
          <Badge className={`${SEVERITY_BADGE[alert.severity]} capitalize`}>
            {alert.severity}
          </Badge>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {ALERT_TYPE_LABEL[alert.alert_type]}
          </span>
        </div>

        <div className="flex-1">
          <p className="font-medium">{alert.message}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>{projectName}</span>
            <span>·</span>
            <span title={alert.created_at}>{relative}</span>
            {alert.source_dsr_id && (
              <>
                <span>·</span>
                <button
                  type="button"
                  onClick={() => onOpenSourceDsr(alert.source_dsr_id!)}
                  className="text-mozaic-teal hover:underline"
                >
                  View source DSR
                </button>
              </>
            )}
            {isAck && (
              <>
                <span>·</span>
                <span className="text-green-700">
                  Acknowledged {formatDistanceToNow(new Date(alert.acknowledged_at!), { addSuffix: true })}
                </span>
              </>
            )}
          </div>
        </div>

        {!isAck && (
          <div className="shrink-0">
            <Button size="sm" variant="outline" onClick={onAck} disabled={acking}>
              <Check className="mr-1 h-4 w-4" />
              Acknowledge
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
